import { useState, useEffect } from "react";
import axios from "axios";
import { X, Search, Plus } from "lucide-react";
import { J, MANROPE, FOCUS } from "./journeyTokens";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/* §11 — start a journey for a client that isn't on the board yet. */
export const StartJourneyDialog = ({ existingIds, onClose, onStarted, onError }) => {
  const [clients, setClients] = useState(null);
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(null);

  useEffect(() => {
    axios.get(`${API}/customer-success/clients`)
      .then((r) => setClients(r.data.clients || []))
      .catch(() => { setClients([]); onError("Could not load the clients"); });
  }, []);   // eslint-disable-line react-hooks/exhaustive-deps

  const t = q.trim().toLowerCase();
  const choices = (clients || [])
    .filter((c) => !existingIds.has((c.client_id || "").toUpperCase()))
    .filter((c) => !t || `${c.company_name} ${c.client_id}`.toLowerCase().includes(t))
    .slice(0, 50);

  const start = async (c) => {
    setBusy(c.client_id);
    try {
      await axios.put(`${API}/customer-success/journeys/${encodeURIComponent(c.client_id)}`, {
        company_name: c.company_name || c.client_id,
        status: "onboarding",
      });
      onStarted(c);
    } catch (e) {
      onError(e?.response?.data?.detail || "Could not start that journey");
      setBusy(null);
    }
  };

  return (
    <div className="fixed inset-0 z-[80] flex items-start justify-center px-4 py-10" data-testid="journey-start-dialog">
      <div className="absolute inset-0" style={{ background: "rgba(18,18,18,0.28)" }} onClick={onClose} />
      <div className="relative rounded-[12px] w-full max-w-[520px] flex flex-col overflow-hidden"
        style={{ background: J.card, border: `1px solid ${J.edge}`, maxHeight: "80vh" }}>
        <div className="px-5 py-4 flex items-start justify-between gap-3" style={{ borderBottom: `1px solid ${J.rule}` }}>
          <div>
            <div style={{ ...MANROPE, fontSize: 17, fontWeight: 800, color: J.ink }}>Start a journey</div>
            <div className="text-[12.5px]" style={{ color: J.ink2 }}>Pick the client that is starting onboarding.</div>
          </div>
          <button onClick={onClose} data-testid="journey-start-close"
            className="w-8 h-8 rounded-[7px] flex items-center justify-center hover:bg-[#F5F2EA]"
            style={{ color: J.ink2 }}><X className="w-4 h-4" /></button>
        </div>

        <div className="px-5 py-3" style={{ borderBottom: `1px solid ${J.rule}`, background: J.head }}>
          <div className="relative">
            <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2" style={{ color: J.muted }} />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search clients" autoFocus
              className={`h-[34px] w-full rounded-[8px] pl-8 pr-2.5 text-[13px] bg-white ${FOCUS}`}
              style={{ border: `1px solid ${J.edge}`, color: J.ink }} data-testid="journey-start-search" />
          </div>
        </div>

        <div className="flex-1 overflow-y-auto">
          {clients === null ? (
            [0, 1, 2, 3].map((i) => (
              <div key={i} className="animate-pulse" style={{ height: 52, borderBottom: `1px solid ${J.rule}`, background: "#FBFAF7" }} />
            ))
          ) : choices.length === 0 ? (
            <div className="px-5 py-10 text-center text-[13px]" style={{ color: J.muted }} data-testid="journey-start-empty">
              {t ? "No clients match that search." : "Every client is already on the board."}
            </div>
          ) : choices.map((c) => (
            <button key={c.client_id} onClick={() => start(c)} disabled={!!busy}
              data-testid={`journey-start-choice-${c.client_id}`}
              className="w-full flex items-center justify-between gap-3 px-5 py-3 text-left hover:bg-[#FBFAF7] disabled:opacity-50"
              style={{ borderBottom: `1px solid ${J.rule}` }}>
              <div className="min-w-0">
                <div className="text-[13.5px] font-semibold truncate" style={{ color: J.ink }}>{c.company_name}</div>
                <div className="mono text-[11px]" style={{ color: J.muted }}>{c.client_id}</div>
              </div>
              <Plus className="w-4 h-4 shrink-0" style={{ color: "#8A6D00" }} />
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};
